<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDocCategories } from '@/composables/useDocCategories'
import { useDocSearch } from '@/composables/useDocSearch'
import { useDocsContext } from '@/composables/useDocsContext'
import { useLocale } from '@/composables/useLocale'
import { DocCategoryValues } from '@/types/docs'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import DocsPublicLayout from '@/components/layout/DocsPublicLayout.vue'
import DocsCategoryCard from '@/components/common/docs/DocsCategoryCard.vue'
import DocsSearchPalette from '@/components/common/docs/DocsSearchPalette.vue'
import type { DocCategorySummary } from '@/types/docs'

const { t } = useI18n()
const { base, isPublic } = useDocsContext()
const { currentLocale } = useLocale()
const { ensureLoaded } = useDocSearch()

const { categories, loading, error, fetch } = useDocCategories()
const paletteOpen = ref(false)

// Build a lookup map for quick access
const categoryMap = computed<Record<string, DocCategorySummary>>(() => {
  const map: Record<string, DocCategorySummary> = {}
  for (const c of categories.value) {
    map[c.slug] = c
  }
  return map
})

function articleCount(slug: string): number {
  return categoryMap.value[slug]?.article_count ?? 0
}

const hasAnyArticle = computed<boolean>(() => {
  return categories.value.some((c) => c.article_count > 0)
})

function openPalette(): void {
  paletteOpen.value = true
}

function handleGlobalKeydown(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault()
    paletteOpen.value = true
  }
}

onMounted(() => {
  void fetch()
  void ensureLoaded(currentLocale.value)
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})
</script>

<template>
  <component :is="isPublic ? DocsPublicLayout : OrganizerLayout">
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Page heading -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white">
          {{ t('docs.title') }}
        </h1>
        <p class="mt-1 text-slate-400">
          {{ t('docs.subtitle') }}
        </p>
      </div>

      <!-- Search bar -->
      <div class="mb-10">
        <button
          type="button"
          class="w-full max-w-2xl flex items-center gap-3 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-slate-500 px-4 py-3 text-sm text-slate-500 hover:text-slate-300 transition-colors text-left"
          :aria-label="t('docs.hub.search_placeholder')"
          @click="openPalette"
        >
          <svg
            class="w-4 h-4 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <span class="flex-1">{{ t('docs.hub.search_placeholder') }}</span>
          <kbd class="hidden sm:flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700 border border-slate-600 text-xs text-slate-400 font-mono">
            <span>⌘K</span>
          </kbd>
        </button>
      </div>

      <!-- Error banner -->
      <div
        v-if="error"
        class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t('docs.hub.error') }}
      </div>

      <!-- Skeleton loading state -->
      <div
        v-if="loading"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        aria-busy="true"
        aria-label="Loading"
      >
        <div
          v-for="i in 10"
          :key="i"
          class="rounded-xl bg-slate-800/50 border border-slate-700 p-6 animate-pulse"
        >
          <div class="w-10 h-10 rounded-lg bg-slate-700 mb-4" />
          <div class="h-4 bg-slate-700 rounded w-3/4 mb-2" />
          <div class="h-3 bg-slate-700 rounded w-full mb-1" />
          <div class="h-3 bg-slate-700 rounded w-2/3 mb-4" />
          <div class="h-3 bg-slate-700 rounded w-1/3" />
        </div>
      </div>

      <!-- Empty state — no published articles at all -->
      <div
        v-else-if="!loading && !error && !hasAnyArticle && categories.length > 0"
        class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-16 text-center"
      >
        <p class="text-slate-300 text-sm font-medium mb-1">
          {{ t('docs.hub.empty_title') }}
        </p>
        <p class="text-slate-400 text-sm">
          {{ t('docs.hub.empty_body') }}
        </p>
      </div>

      <!-- Category grid -->
      <template v-else-if="!loading">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">
          {{ t('docs.hub.browse_heading') }}
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <DocsCategoryCard
            v-for="slug in DocCategoryValues"
            :key="slug"
            :category="slug"
            :article-count="articleCount(slug)"
            :base="base"
          />
        </div>
      </template>
    </main>

    <!-- Search palette -->
    <DocsSearchPalette
      :open="paletteOpen"
      :base="base"
      @close="paletteOpen = false"
    />
  </component>
</template>
